// Learner Switcher: the perforated ticket for changing active learners.

import { useState, useRef, useEffect } from 'react'
import { useGlobalState } from '@/stores/globalState'
import { useLearners } from '@/hooks/useLearners'
import { hsStageName } from '@/lib/hsStageNames'
import type { Learner } from '@/types'
import { MaskedName } from '@/lib/privacyMask'
import './styles/LearnerSwitcher.css'

function inkClass(index: number): string {
  return `is-ink-${index % 4}`
}

function Avatar({ initial, inkIndex, size = 32 }: { initial: string; inkIndex: number; size?: number }) {
  return (
    <span
      className={`learner-switcher__avatar ${inkClass(inkIndex)}${size > 32 ? ' is-large' : ''}`}
    >
      {initial}
    </span>
  )
}

function Check() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 20 20"
      className="learner-switcher__check"
      aria-hidden="true"
    >
      <path d="M4 11l4 4 8-9" />
    </svg>
  )
}

export function LearnerSwitcher() {
  const [isOpen, setIsOpen] = useState(false)
  const sheetRef = useRef<HTMLDivElement>(null)

  const activeLearner = useGlobalState((s) => s.activeLearner)
  const setActiveLearner = useGlobalState((s) => s.setActiveLearner)
  const setAddLearnerOpen = useGlobalState((s) => s.setAddLearnerOpen)
  const { learners, isLoading } = useLearners()

  useEffect(() => {
    if (!isOpen) return
    function onPointerDown(e: MouseEvent) {
      if (sheetRef.current && !sheetRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') setIsOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [isOpen])

  const handleSelect = (learner: Learner) => {
    setActiveLearner(learner.id, learner)
    setIsOpen(false)
  }

  const activeIndex = Math.max(0, learners.findIndex((l) => l.id === activeLearner?.id))

  return (
    <div className="hs-brand learner-switcher">
      <div className="learner-switcher__slot">
        <div
          ref={sheetRef}
          className={isOpen ? 'learner-switcher__sheet is-open' : 'learner-switcher__sheet'}
        >
          <button
            type="button"
            onClick={() => setIsOpen((o) => !o)}
            aria-expanded={isOpen}
            aria-label="Switch learner"
            className="learner-switcher__trigger"
          >
            {activeLearner ? (
              <Avatar initial={activeLearner.name[0]} inkIndex={activeIndex} size={34} />
            ) : (
              <span className="learner-switcher__avatar-placeholder" />
            )}
            <div className="learner-switcher__learner-copy">
              <div className="learner-switcher__learner-name">
                <MaskedName>{activeLearner?.name ?? 'Choose a learner'}</MaskedName>
              </div>
              <div className="learner-switcher__learner-stage is-active">
                {activeLearner ? hsStageName(activeLearner) : ' '}
              </div>
            </div>
            <svg
              width="14"
              height="14"
              viewBox="0 0 20 20"
              className={isOpen ? 'learner-switcher__chevron is-open' : 'learner-switcher__chevron'}
              aria-hidden="true"
            >
              <path d="M7 4l6 6-6 6" />
            </svg>
          </button>

          {isOpen && (
            <>
              <div className="learner-switcher__perforation" />
              <div className="learner-switcher__menu">
                <div className="learner-switcher__menu-label">Switch learner</div>

                {isLoading ? (
                  <div className="learner-switcher__loading">Loading...</div>
                ) : (
                  learners.map((learner, i) => {
                    const current = learner.id === activeLearner?.id

                    if (current) {
                      return (
                        <div key={learner.id} className="hs-stamp learner-switcher__row is-current">
                          <Avatar initial={learner.name[0]} inkIndex={i} />
                          <div className="learner-switcher__learner-copy">
                            <div className="learner-switcher__learner-name is-current">
                              <MaskedName>{learner.name}</MaskedName>
                            </div>
                            <div className="learner-switcher__learner-stage is-current">
                              {hsStageName(learner)}
                            </div>
                          </div>
                          <Check />
                        </div>
                      )
                    }

                    return (
                      <button
                        key={learner.id}
                        type="button"
                        onClick={() => handleSelect(learner)}
                        className="learner-switcher__row"
                      >
                        <Avatar initial={learner.name[0]} inkIndex={i} />
                        <div className="learner-switcher__learner-copy">
                          <div className="learner-switcher__learner-name">
                            <MaskedName>{learner.name}</MaskedName>
                          </div>
                          <div className={`learner-switcher__learner-stage ${inkClass(i)}`}>
                            {hsStageName(learner)}
                          </div>
                        </div>
                      </button>
                    )
                  })
                )}

                <div className="learner-switcher__divider" />

                <button
                  type="button"
                  onClick={() => {
                    setIsOpen(false)
                    setAddLearnerOpen(true)
                  }}
                  className="learner-switcher__row"
                >
                  <span className="learner-switcher__add-icon">
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 14 14"
                      className="learner-switcher__plus"
                      aria-hidden="true"
                    >
                      <path d="M7 2v10M2 7h10" />
                    </svg>
                  </span>
                  <div className="learner-switcher__add-label">Add a learner</div>
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
